import { Banknote, CreditCard, Smartphone, Wallet } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui";
import { EmptyState } from "@/components/common";
import { formatCurrency } from "@/lib/utils";
import type { PaymentMethod } from "@/types";

interface PaymentMixCardProps {
  data: Record<PaymentMethod, number>;
}

const ROWS: { method: PaymentMethod; label: string; icon: React.ElementType }[] =
  [
    { method: "cash", label: "Cash", icon: Banknote },
    { method: "card", label: "Card", icon: CreditCard },
    { method: "mobile", label: "Mobile", icon: Smartphone },
  ];

export function PaymentMixCard({ data }: PaymentMixCardProps) {
  const total = ROWS.reduce(
    (sum, row) => sum + (data[row.method] || 0),
    0,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wallet className="h-5 w-5" />
          Payment mix
        </CardTitle>
        <CardDescription>How customers paid</CardDescription>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <EmptyState
            icon={Wallet}
            title="No payments yet"
            description="Once orders are paid the breakdown shows here"
          />
        ) : (
          <ul className="space-y-3">
            {ROWS.map(({ method, label, icon: Icon }) => {
              const amount = data[method] || 0;
              const share = total > 0 ? amount / total : 0;

              return (
                <li key={method} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 font-medium">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      {label}
                    </span>
                    <span className="text-muted-foreground">
                      {formatCurrency(amount)} · {(share * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full bg-primary"
                      style={{ width: `${(share * 100).toFixed(1)}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
